/* ============================================================================
   rcds-article.js — RolaCase US Design System, BLOG ARTICLE interactivity
   ----------------------------------------------------------------------------
   Ports Nabila blog.js:
     · TOC scroll-spy (blog.js:31-81)  -> IntersectionObserver, active = is-rcds-on
     · lazy video facade (blog.js:11-28)
     · working share-intent hrefs built from the live article URL (Nabila's were
       href="#" placeholders)
   NO reading-progress bar (absent in the source, out of scope).

   All hooks are namespaced data-rcds-*:
     [data-rcds-article-body]        the prose+components column the TOC scans
     [data-rcds-toc-list]            desktop rail <ul> (populated here)
     [data-rcds-toc-mobile-list]     mobile dropdown <ol> (populated here)
     [data-rcds-toc-cur]             mobile "current section" label
     [data-rcds-toc]="Label"         a component section that is a TOC entry but
                                     has no <h2> of its own (answer / video / …)
     [data-rcds-video]  .rcds-video-embed__frame[data-rcds-embed="<url>"]
     [data-rcds-share]="facebook|linkedin|x|copy"
     [data-rcds-share-feedback]      aria-live target for the copy confirmation

   Load with defer, AFTER the theme's own scripts:
     <script src="{{ 'rcds-article.js' | asset_url }}" defer></script>
   Every lookup is guarded, so the file is inert on pages without an article.
   ============================================================================ */
(function () {
  'use strict';

  var $ = function (sel, root) { return (root || document).querySelector(sel); };
  var $$ = function (sel, root) { return Array.prototype.slice.call((root || document).querySelectorAll(sel)); };

  /* header/sticky offset in the zoomed content coordinate space. The rcds sticky
     header is ~ (promo 30 + bar 60) design px; in this space that reads ~104.
     Kept as one constant so the anchor scroll and the observer rootMargin agree. */
  var SCROLL_OFFSET = 104;

  /* --------------------------------------------------------------------------
     1. Lazy video facade — inject the iframe only on user intent.
     -------------------------------------------------------------------------- */
  function initVideo() {
    $$('[data-rcds-video] .rcds-video-embed__frame[data-rcds-embed]').forEach(function (frame) {
      function load() {
        if (frame.dataset.rcdsLoaded) { return; }
        frame.dataset.rcdsLoaded = '1';
        var src = frame.getAttribute('data-rcds-embed');
        if (!src) { return; }
        var ifr = document.createElement('iframe');
        ifr.src = src + (src.indexOf('?') > -1 ? '&' : '?') + 'autoplay=1';
        ifr.title = frame.getAttribute('aria-label') || 'Video';
        ifr.setAttribute('allow', 'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture');
        ifr.setAttribute('allowfullscreen', '');
        ifr.setAttribute('loading', 'lazy');
        frame.innerHTML = '';
        frame.appendChild(ifr);
      }
      frame.addEventListener('click', load);
      frame.addEventListener('keydown', function (e) {
        if (e.key === 'Enter' || e.key === ' ' || e.key === 'Spacebar') { e.preventDefault(); load(); }
      });
    });
  }

  /* --------------------------------------------------------------------------
     2. TOC — built client-side from the rendered article body, then scroll-spy.
        Candidates, in document order:
          · any element carrying [data-rcds-toc] (component sections with a label
            but no <h2> — the short-answer box, the video, the spec table)
          · any <h2> that is NOT already inside a [data-rcds-toc] section
        Each candidate is given a namespaced id if it lacks one, so no bare id is
        minted onto the page (keeps the live-collision gate honest at runtime too).
     -------------------------------------------------------------------------- */
  function slugify(text) {
    return String(text || '')
      .toLowerCase()
      .replace(/[^\w]+/g, '-')
      .replace(/^-+|-+$/g, '')
      .slice(0, 48) || 'section';
  }

  function pad2(n) { return (n < 10 ? '0' : '') + n; }

  function collectEntries(body) {
    var seen = Object.create(null);
    var nodes = $$('[data-rcds-toc], h2', body);
    var entries = [];
    nodes.forEach(function (node) {
      var isSection = node.hasAttribute('data-rcds-toc');
      var isLooseH2 = node.tagName === 'H2' && !node.closest('[data-rcds-toc]');
      if (!isSection && !isLooseH2) { return; }

      var label = '';
      if (isSection) {
        label = (node.getAttribute('data-rcds-toc') || '').trim();
        if (!label) {
          var h = node.querySelector('h2');
          label = h ? h.textContent.trim() : '';
        }
      } else {
        label = node.textContent.trim();
      }
      if (!label) { return; }

      var id = node.id;
      if (!id) {
        id = 'rcds-s-' + slugify(label);
        var base = id, i = 2;
        while (document.getElementById(id) || seen[id]) { id = base + '-' + (i++); }
        node.id = id;
      }
      if (seen[id]) { return; }
      seen[id] = true;
      entries.push({ id: id, label: label });
    });
    return entries;
  }

  // build one <a> with a numbered <span> + a text-node label. Every value is set
  // via textContent / createTextNode, never innerHTML, so a component-supplied
  // data-rcds-toc label can never inject markup.
  function makeLink(numClass, num, id, label) {
    var a = document.createElement('a');
    a.href = '#' + id;
    a.setAttribute('data-rcds-toc-link', '');
    a.setAttribute('data-rcds-label', label);
    var n = document.createElement('span');
    n.className = numClass;
    n.textContent = num;
    a.appendChild(n);
    a.appendChild(document.createTextNode(label));
    return a;
  }

  function buildLists(entries, deskList, mobList) {
    entries.forEach(function (e, idx) {
      var num = pad2(idx + 1);
      if (deskList) {
        var li = document.createElement('li');
        var a = makeLink('rcds-toc__num', num, e.id, e.label);
        a.className = 'rcds-toc__link';
        li.appendChild(a);
        deskList.appendChild(li);
      }
      if (mobList) {
        var mli = document.createElement('li');
        mli.appendChild(makeLink('rcds-toc-mobile__num', num, e.id, e.label));
        mobList.appendChild(mli);
      }
    });
  }

  function initTOC() {
    var body = $('[data-rcds-article-body]');
    if (!body) { return; }
    var deskList = $('[data-rcds-toc-list]');
    var mobList = $('[data-rcds-toc-mobile-list]');
    if (!deskList && !mobList) { return; }

    var entries = collectEntries(body);
    if (!entries.length) {
      // nothing to anchor: hide the empty rails so no dead chrome ships
      var toc = $('.rcds-toc'); if (toc) { toc.style.display = 'none'; }
      var tocM = $('.rcds-toc-mobile'); if (tocM) { tocM.style.display = 'none'; }
      return;
    }

    buildLists(entries, deskList, mobList);

    var links = $$('[data-rcds-toc-link]');
    var curLabel = $('[data-rcds-toc-cur]');
    var sections = entries.map(function (e) { return document.getElementById(e.id); }).filter(Boolean);

    function setActive(id) {
      links.forEach(function (a) {
        a.classList.toggle('is-rcds-on', a.getAttribute('href') === '#' + id);
      });
      var active = links.filter(function (a) { return a.getAttribute('href') === '#' + id; })[0];
      if (curLabel && active) { curLabel.textContent = active.getAttribute('data-rcds-label') || active.textContent.trim(); }
    }

    if ('IntersectionObserver' in window) {
      var visible = new Map();
      var io = new IntersectionObserver(function (ents) {
        ents.forEach(function (en) {
          if (en.isIntersecting) { visible.set(en.target.id, en.intersectionRatio); }
          else { visible.delete(en.target.id); }
        });
        var best = null, bestTop = Infinity;
        visible.forEach(function (_, id) {
          var el = document.getElementById(id);
          if (!el) { return; }
          var top = el.getBoundingClientRect().top;
          if (top < bestTop) { bestTop = top; best = id; }
        });
        if (best) { setActive(best); }
      }, { rootMargin: '-118px 0px -62% 0px', threshold: [0, 1] });
      sections.forEach(function (s) { io.observe(s); });
    }

    // smooth scroll + close the mobile dropdown on click
    links.forEach(function (a) {
      a.addEventListener('click', function (e) {
        var id = a.getAttribute('href').slice(1);
        var t = document.getElementById(id);
        if (!t) { return; }
        e.preventDefault();
        var y = t.getBoundingClientRect().top + window.scrollY - SCROLL_OFFSET;
        window.scrollTo({ top: y, behavior: 'smooth' });
        setActive(id);
        var dd = a.closest('.rcds-toc-mobile');
        if (dd) { var details = dd.querySelector('details'); if (details) { details.open = false; } }
        if (history.replaceState) { history.replaceState(null, '', '#' + id); }
      });
    });

    setActive(sections[0].id);
  }

  /* --------------------------------------------------------------------------
     3. Share intents — real hrefs from the canonical article URL.
     -------------------------------------------------------------------------- */
  function articleUrl() {
    var canon = $('link[rel="canonical"]');
    return (canon && canon.href) || window.location.href.split('#')[0];
  }

  function initShare() {
    var links = $$('[data-rcds-share]');
    if (!links.length) { return; }
    var url = articleUrl();
    var enc = encodeURIComponent(url);
    var title = encodeURIComponent(document.title || 'RolaCase Field Guide');
    var feedback = $('[data-rcds-share-feedback]');

    links.forEach(function (a) {
      var kind = (a.getAttribute('data-rcds-share') || '').toLowerCase();
      if (kind === 'facebook') {
        a.href = 'https://www.facebook.com/sharer/sharer.php?u=' + enc;
        a.target = '_blank'; a.rel = 'noopener noreferrer';
      } else if (kind === 'linkedin') {
        a.href = 'https://www.linkedin.com/sharing/share-offsite/?url=' + enc;
        a.target = '_blank'; a.rel = 'noopener noreferrer';
      } else if (kind === 'x' || kind === 'twitter') {
        a.href = 'https://twitter.com/intent/tweet?url=' + enc + '&text=' + title;
        a.target = '_blank'; a.rel = 'noopener noreferrer';
      } else if (kind === 'copy') {
        a.href = url;
        a.addEventListener('click', function (e) {
          e.preventDefault();
          var done = function () { if (feedback) { feedback.textContent = 'Link copied'; setTimeout(function () { feedback.textContent = ''; }, 2400); } };
          if (navigator.clipboard && navigator.clipboard.writeText) {
            navigator.clipboard.writeText(url).then(done, done);
          } else {
            done();
          }
        });
      }
    });
  }

  function boot() { initVideo(); initTOC(); initShare(); }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
